import { cn } from "@/lib/utils";
import {
  DASHBOARD_LAYOUT_VARIANTS,
  DashboardLayoutVariant,
} from "@/types/dashboard/genie-console";

interface LayoutSwitcherProps {
  active: DashboardLayoutVariant;
  onChange: (variant: DashboardLayoutVariant) => void;
}

const LAYOUT_LABELS: Record<DashboardLayoutVariant, string> = {
  spine: "Spine",
  stage: "Stage",
  filmstrip: "Filmstrip",
};

const LAYOUT_BLURBS: Record<DashboardLayoutVariant, string> = {
  spine: "Gradient rail, cards hanging off it",
  stage: "Full-bleed brand washes, magazine spread",
  filmstrip: "Fixed summary rail, content scrolls sideways",
};

/**
 * Review-only switcher for the HG-1225 layout exploration.
 *
 * It renders ONLY when `?layout=` is already in the URL, so a normal user
 * never sees it — open `/dashboard?layout=spine` on the branch deploy and all
 * three directions become one click apart.
 */
export const LayoutSwitcher = ({ active, onChange }: LayoutSwitcherProps) => (
  <div className="mb-6 rounded-2xl border border-dashed border-genie-border bg-white px-4 py-3">
    <p className="mb-2 text-xs font-semibold uppercase tracking-[1.5px] text-[#64748B]">
      Layout direction — review only
    </p>
    <div className="flex flex-wrap gap-2">
      {DASHBOARD_LAYOUT_VARIANTS.map((variant) => (
        <button
          key={variant}
          type="button"
          onClick={() => onChange(variant)}
          aria-pressed={variant === active}
          className={cn(
            "rounded-xl border px-3.5 py-2 text-left transition-all",
            variant === active
              ? "border-genie-primary bg-genie-primary/5"
              : "border-gray-200 hover:border-gray-300",
          )}
        >
          <span
            className={cn(
              "block text-sm font-semibold",
              variant === active ? "text-genie-primary" : "text-[#222B45]",
            )}
          >
            {LAYOUT_LABELS[variant]}
          </span>
          <span className="block text-xs text-gray-500">{LAYOUT_BLURBS[variant]}</span>
        </button>
      ))}
    </div>
  </div>
);

export default LayoutSwitcher;
